/**
 * bulkTransaction — Sprint 3 (F5E-001)
 *
 * Wrapper para operaciones masivas que deben ser atómicas por lotes.
 * Firestore limita a 500 writes por batch/transaction, así que dividimos
 * en chunks y registramos progreso. Si un chunk falla, se detiene y reporta.
 *
 * NOTA: Firestore batches NO son transactions reales (no hay rollback
 * cross-batch). Para atomicidad total usar runTransaction con un solo batch
 * de <=500 docs. Este helper es para "best-effort bulk con trazabilidad".
 */

import { writeBatch, runTransaction, Transaction } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { logAudit } from './auditLog';

export type BulkOperation =
  | { type: 'set'; ref: any; data: any; merge?: boolean }
  | { type: 'update'; ref: any; data: any }
  | { type: 'delete'; ref: any };

export interface BulkExecuteOptions {
  operation: string;
  userId?: string;
  userEmail?: string;
  userRol?: string;
  /** Tamaño de chunk. Default 400 (bajo el límite 500 por seguridad). */
  chunkSize?: number;
  /** Progress callback. */
  onProgress?: (completed: number, total: number) => void;
  /** Audit metadata adicional. */
  metadata?: Record<string, any>;
}

export interface BulkResult {
  success: boolean;
  completed: number;
  total: number;
  chunksCompleted: number;
  chunksFailed: number;
  errors: Array<{ chunkIndex: number; error: string }>;
}

/**
 * Ejecuta operaciones en batches. No es atómico cross-batch pero cada batch sí.
 */
export async function bulkBatch(
  operations: BulkOperation[],
  options: BulkExecuteOptions
): Promise<BulkResult> {
  const chunkSize = options.chunkSize ?? 400;
  const total = operations.length;
  const chunks: BulkOperation[][] = [];
  for (let i = 0; i < total; i += chunkSize) chunks.push(operations.slice(i, i + chunkSize));

  const result: BulkResult = {
    success: true,
    completed: 0,
    total,
    chunksCompleted: 0,
    chunksFailed: 0,
    errors: [],
  };

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    const batch = writeBatch(db);
    try {
      for (const op of chunk) {
        if (op.type === 'set') batch.set(op.ref, op.data, { merge: op.merge ?? false });
        else if (op.type === 'update') batch.update(op.ref, op.data);
        else if (op.type === 'delete') batch.delete(op.ref);
      }
      await batch.commit();
      result.chunksCompleted++;
      result.completed += chunk.length;
      options.onProgress?.(result.completed, total);
    } catch (e: any) {
      result.success = false;
      result.chunksFailed++;
      result.errors.push({ chunkIndex: i, error: e?.message || String(e) });
      // Detener en primer fallo para evitar daño parcial. Caller decide reintentar.
      break;
    }
  }

  await logAudit({
    action: 'bulk_operation',
    collection: 'multiple',
    userId: options.userId,
    userEmail: options.userEmail,
    userRol: options.userRol,
    metadata: {
      operation: options.operation,
      ...options.metadata,
      completed: result.completed,
      total,
      chunksCompleted: result.chunksCompleted,
      chunksFailed: result.chunksFailed,
      errors: result.errors.slice(0, 10), // primeros 10 errores
    },
  });

  return result;
}

/**
 * Ejecuta operaciones pequeñas (<=500) en una transaction atómica real.
 * Usar para operaciones donde todo-o-nada es crítico (ej: reubicar miembro).
 */
export async function bulkTransaction<T>(
  fn: (tx: Transaction) => Promise<T>,
  options: BulkExecuteOptions
): Promise<T> {
  try {
    const result = await runTransaction(db, fn);
    await logAudit({
      action: 'bulk_operation',
      collection: 'multiple',
      userId: options.userId,
      userEmail: options.userEmail,
      userRol: options.userRol,
      metadata: {
        operation: options.operation,
        ...options.metadata,
        atomic: true,
      },
    });
    return result;
  } catch (e: any) {
    await logAudit({
      action: 'bulk_operation',
      collection: 'multiple',
      userId: options.userId,
      userEmail: options.userEmail,
      userRol: options.userRol,
      metadata: {
        operation: options.operation,
        ...options.metadata,
        atomic: true,
        failed: true,
        error: e?.message || String(e),
      },
    });
    throw e;
  }
}
